import React, { useEffect, useMemo, useState } from 'react';

import { faClose, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';

import { useGetRolesQuery } from 'apis/roles.api';
import { ReactComponent as LockSVG } from 'assets/svg/lock.svg';
import { ReactComponent as UnlockSVG } from 'assets/svg/unlocked.svg';
import { ButtonSizeVariants, ButtonVariants } from 'constants/common';
import {
  AggregateFunctionType,
  Condition,
  FILTER_CONDITIONS_FOR_BOOLEANS,
  FILTER_CONDITIONS_FOR_NEW_FILTER,
  FILTER_CONDITIONS_FOR_NUMBERS,
  FILTER_CONDITIONS_FOR_STRINGS,
  FilterItem,
  parseQualifiedColumn,
  qualifyColumn,
} from 'constants/reports-templates';
import useTenantId from 'hooks/useTenantId';
import { ReportingColumn } from 'interfaces/report-configuration.interface';
import { VisualizationItemPayload } from 'interfaces/report-templates.interface';
import { Input, SelectFormInput, Button, InputNumberFormat } from 'shared-components';
import DatePickerInput from 'shared-components/DatePickerInput';
import ModalControlButtons from 'shared-components/ModalControlButtons';

type Props = {
  selectedColumnsData?: ReportingColumn[];
  control: any;
  selectedTable: string | null;
  onCloseModal: VoidFunction;
  onSubmit: (filters: Omit<FilterItem, 'id'>[]) => void;
  initialFilters?: FilterItem[];
  grouping?: VisualizationItemPayload | null;
  aggregates?: { func: AggregateFunctionType; column?: string }[];
};

const CreateReportTemplateFiltersModal: React.FC<Props> = ({
  selectedColumnsData = [],
  control,
  selectedTable,
  onCloseModal,
  onSubmit,
  initialFilters = [],
  grouping,
  aggregates = [],
}) => {
  const { t } = useTranslation();
  const { setValue, getValues } = useFormContext();
  const watchedFormValues = useWatch({ control });

  const { tenantIdNumeric, tenantIdQueryParameter, shouldSkip } = useTenantId({ onError: () => undefined });

  const { data: rolesData } = useGetRolesQuery(
    { params: `MaxResultCount=100${tenantIdQueryParameter}`, isAdmin: !!tenantIdNumeric },
    { skip: shouldSkip }
  );

  const rolesOptions = useMemo(
    () => (rolesData?.result?.items ?? []).map((r) => ({ label: r.displayName, value: String(r.id) })),
    [rolesData]
  );

  const createEmptyFilter = (): FilterItem => ({
    id: crypto.randomUUID(),
    column: null,
    condition: null,
    value: '',
    locked: false,
    operator: 'And',
  });

  const [filters, setFilters] = useState<FilterItem[]>(() =>
    initialFilters?.length
      ? initialFilters.map((f) => ({ ...f, id: f.id ?? crypto.randomUUID() }))
      : [createEmptyFilter()]
  );
  const getConditionsByType = (type?: number) => {
    switch (type) {
      case 5:
        return FILTER_CONDITIONS_FOR_NUMBERS;
      case 4:
        return FILTER_CONDITIONS_FOR_BOOLEANS;
      case 3:
        return FILTER_CONDITIONS_FOR_NUMBERS;
      case 1:
      case 2:
        return FILTER_CONDITIONS_FOR_STRINGS;
      default:
        return [];
    }
  };

  const availableColumns = useMemo(() => {
    const allColumns = selectedColumnsData.filter((col) => col.tableName === selectedTable);
    const hasGrouping = !!grouping;
    const hasAggregates = aggregates.length > 0;

    if (!hasGrouping && !hasAggregates) {
      return allColumns;
    }

    const filtered: ReportingColumn[] = [];

    if (hasGrouping) {
      const groupCol = allColumns.find((col) => col.name === grouping.name);
      if (groupCol) {
        filtered.push(groupCol);
      }
    }

    for (const agg of aggregates) {
      const label = agg.column ? `${agg.func} ${agg.column}` : agg.func;
      filtered.push({
        name: label,
        displayName: label,
        tableName: selectedTable ?? '',
        columnType: 3,
        displayType: 'Number',
      } as ReportingColumn);
    }

    const otherTableColumns = selectedColumnsData.filter((col) => col.tableName !== selectedTable);
    filtered.push(...otherTableColumns);

    return filtered;
  }, [selectedColumnsData, selectedTable, grouping, aggregates]);

  const updateFilter = (id: string | number, updates: Partial<FilterItem>) => {
    setFilters((prev) =>
      prev.map((filter) => {
        if (String(filter.id) === String(id)) {
          const hasActualChanges = Object.keys(updates).some(
            (key) => String(updates[key as keyof FilterItem]) !== String(filter[key as keyof FilterItem])
          );
          return hasActualChanges ? { ...filter, ...updates } : filter;
        }
        return filter;
      })
    );
  };

  const isSaveDisabled = filters.some((filter) => {
    if (!filter.column || filter.condition === null) {
      return true;
    }
    if (filter.condition === Condition.IsNull || filter.condition === Condition.IsNotNull) {
      return false;
    }
    const column = resolveColumn(filter.column);
    if (column?.columnType === 5) {
      return !watchedFormValues?.[`dateValue-${filter.id}`];
    }
    if (column?.columnType === 3) {
      const numVal = watchedFormValues?.[`numberValue-${filter.id}`];
      return numVal === null || numVal === undefined || String(numVal) === '';
    }
    return !filter.value;
  });

  function resolveColumn(colName?: string) {
    if (!colName) {
      return undefined;
    }

    const { table, column: name } = parseQualifiedColumn(colName);

    if (table) {
      return (
        availableColumns.find((c) => c.tableName === table && c.name === name) ||
        selectedColumnsData.find((c) => c.tableName === table && c.name === name)
      );
    }

    // Unqualified: prefer selected table column, then available columns
    return (
      selectedColumnsData.find((c) => c.name === name && c.tableName === selectedTable) ??
      availableColumns.find((c) => c.name === name)
    );
  }

  const renderValueInput = (filter: FilterItem) => {
    const column = resolveColumn(filter.column as string);

    switch (column?.columnType) {
      case 5:
        return (
          <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <DatePickerInput
              className="w-full"
              name={`dateValue-${filter.id}`}
              control={control}
              inputClassName="w-48"
            />
          </div>
        );
      case 4:
        return (
          <SelectFormInput
            control={control}
            name={`boolValue-${filter.id}`}
            options={[
              { label: 'true', value: 'true' },
              { label: 'false', value: 'false' },
            ]}
            onSelectValue={(opt) => updateFilter(filter.id, { value: opt?.value ?? '' })}
          />
        );
      case 3: {
        const isRoleColumn = Boolean(column?.name && String(column.name).toLowerCase() === 'roleid');
        const isEqualityCondition = filter.condition === Condition.Is || filter.condition === Condition.IsNot;

        if (isRoleColumn && isEqualityCondition) {
          return (
            <SelectFormInput
              control={control}
              name={`numberValue-${filter.id}`}
              options={rolesOptions}
              inputClassName="w-40"
              onSelectValue={(opt) => updateFilter(filter.id, { value: opt?.value ?? '' })}
            />
          );
        }

        return (
          <InputNumberFormat
            name={`numberValue-${filter.id}`}
            control={control}
            value={
              filter.value === '' || filter.value === null || filter.value === undefined ? undefined : filter.value
            }
            isShownArrowButtons
            fixedDecimalLength={0}
            inputClassName="w-40"
            onChange={(value) => updateFilter(filter.id, { value: value ?? '' })}
          />
        );
      }
      case 2:
        return (
          <Input
            name={`stringValue-${filter.id}`}
            value={filter.value ?? ''}
            onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
            inputClassName="!w-48 !text-black-900 !dark:text-white-00 !text-bodyMd"
          />
        );
      default:
        return null;
    }
  };
  // Helpers to keep the effect small (reduce cognitive complexity)
  const getColumnAndOptionValueForFilter = (filter: FilterItem): { column?: ReportingColumn; optionValue?: string } => {
    if (!filter.column || typeof filter.column !== 'string') {
      return {};
    }
    const { table: tbl, column: name } = parseQualifiedColumn(filter.column);
    if (tbl) {
      const column = selectedColumnsData.find((c) => c.tableName === tbl && c.name === name);
      const optionValue = column ? qualifyColumn(column.tableName, column.name) : filter.column;
      return { column, optionValue };
    }

    let column = selectedColumnsData.find((c) => c.name === filter.column && c.tableName === selectedTable);
    if (!column) {
      column = selectedColumnsData.find((c) => c.name === filter.column);
    }
    const optionValue = column ? qualifyColumn(column.tableName, column.name) : filter.column;
    return { column, optionValue };
  };

  const applyOptionValue = (filter: FilterItem, column?: ReportingColumn, optionValue?: string) => {
    if (!optionValue) {
      return;
    }
    setValue(`column-${filter.id}`, {
      label: column?.name ?? String(filter.column),
      value: optionValue,
    });
    updateFilter(filter.id, { column: optionValue });
  };

  const applyConditionValue = (filter: FilterItem, column?: ReportingColumn) => {
    if (filter.condition === null || !column) {
      return;
    }
    const conditionList = getConditionsByType(column.columnType);
    const selectedCondition = conditionList.find((cond) => cond.id === filter.condition);
    if (selectedCondition) {
      setValue(`condition-${filter.id}`, {
        label: selectedCondition.label,
        value: String(selectedCondition.id),
      });
    }
  };

  const applyOperatorValue = (filter: FilterItem, index: number) => {
    if (filter.operator && index > 0) {
      setValue(`operator-${filter.id}`, { label: filter.operator, value: filter.operator });
    }
  };

  const applyTypedValueForColumn = (filter: FilterItem, column?: ReportingColumn) => {
    if (!column) {
      return;
    }
    switch (column.columnType) {
      case 3:
        setValue(`numberValue-${filter.id}`, filter.value);
        break;
      case 5:
        setValue(`dateValue-${filter.id}`, filter.value);
        break;
      case 4:
        setValue(`boolValue-${filter.id}`, filter.value);
        break;
      case 2:
        setValue(`stringValue-${filter.id}`, filter.value);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!filters.length || !selectedColumnsData.length) {
      return;
    }

    filters.forEach((filter, index) => {
      const { column, optionValue } = getColumnAndOptionValueForFilter(filter);
      applyOptionValue(filter, column, optionValue);
      applyConditionValue(filter, column);
      applyOperatorValue(filter, index);
      applyTypedValueForColumn(filter, column);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-3 w-[40rem]">
      {filters.map((filter, index) => (
        <div key={filter.id} className="flex gap-2 items-center w-full">
          <button
            type="button"
            className="min-w-[2.5rem] h-10 flex items-center justify-center border border-neutral-200 dark:border-neutral-800 rounded-md shrink-0 cursor-pointer"
            data-tooltip-id={`tooltip-${filter.id}`}
            onClick={() => updateFilter(filter.id, { locked: !filter.locked })}
          >
            {filter.locked ? <LockSVG className="w-4 h-4" /> : <UnlockSVG className="w-4 h-4" />}
            <Tooltip
              id={`tooltip-${filter.id}`}
              place="top"
              positionStrategy="fixed"
              className="tooltip-styles hidden lg:block !bg-black !opacity-100"
            >
              {filter.locked ? 'Locked filter' : 'Unlocked filter'}
            </Tooltip>
          </button>
          {index > 0 && (
            <SelectFormInput
              control={control}
              name={`operator-${filter.id}`}
              options={FILTER_CONDITIONS_FOR_NEW_FILTER}
              inputClassName="w-full capitalize"
              onSelectValue={(opt) => updateFilter(filter.id, { operator: opt?.value as 'And' | 'Or' })}
            />
          )}
          <SelectFormInput
            control={control}
            name={`column-${filter.id}`}
            inputClassName="!h-10"
            placeholder="Select column"
            options={availableColumns.map((col) => ({
              label: col.name,
              value: qualifyColumn(col.tableName, col.name),
            }))}
            onSelectValue={(opt) =>
              updateFilter(filter.id, {
                column: opt?.value ?? null,
                condition: null,
                value: '',
              })
            }
          />
          {filter.column && (
            <SelectFormInput
              control={control}
              name={`condition-${filter.id}`}
              placeholder="Select condition"
              options={getConditionsByType(resolveColumn(filter.column)?.columnType).map((cond) => ({
                value: String(cond.id),
                label: cond.label,
              }))}
              onSelectValue={(opt) => {
                const condition = Number(opt?.value);
                if (!Number.isNaN(condition)) {
                  updateFilter(filter.id, { condition, value: '' });
                }
              }}
            />
          )}
          {filter.column &&
            filter.condition !== null &&
            filter.condition !== Condition.IsNull &&
            filter.condition !== Condition.IsNotNull &&
            renderValueInput(filter)}

          <FontAwesomeIcon
            icon={faClose}
            onClick={() =>
              setFilters((prev) =>
                prev.length === 1
                  ? [
                      {
                        id: crypto.randomUUID(),
                        column: null,
                        condition: null,
                        value: '',
                        locked: false,
                        operator: 'And',
                      },
                    ]
                  : prev.filter((f) => f.id !== filter.id)
              )
            }
            className="text-neutral-500 w-4 h-4 cursor-pointer"
          />
        </div>
      ))}

      <Button
        className="w-28"
        type="button"
        onClick={() =>
          setFilters((prev) => [
            ...prev,
            { id: crypto.randomUUID(), column: null, condition: null, value: '', locked: false, operator: 'And' },
          ])
        }
        variant={ButtonVariants.OUTLINE}
        size={ButtonSizeVariants.SM}
        disabled={isSaveDisabled}
      >
        <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-1" />
        {t('AddFilter')}
      </Button>

      <div className="flex gap-2 justify-end mt-4">
        <ModalControlButtons
          onClose={onCloseModal}
          onSubmit={() => {
            const resolved = filters.map((filter) => {
              const col = resolveColumn(filter.column as string);
              let value = filter.value;
              if (col?.columnType === 5) {
                value = getValues(`dateValue-${filter.id}`) ?? filter.value;
              }
              if (col?.columnType === 3) {
                value = getValues(`numberValue-${filter.id}`) ?? filter.value;
              }
              if (filter.condition === Condition.IsNull || filter.condition === Condition.IsNotNull) {
                value = '';
              }
              return {
                column: filter.column,
                condition: filter.condition,
                value,
                locked: filter.locked,
                operator: filter.operator,
              };
            });
            onSubmit(resolved);
          }}
          saveButtonText={t('Save')}
          disabled={isSaveDisabled}
        />
      </div>
    </div>
  );
};

export default CreateReportTemplateFiltersModal;
